import { DocumentTypeEnum, FileTypeEnum } from '@/constant';
import { uploadDocument } from '@/services';
import {
	convertAndCompressImages,
	isHeicHeifFile,
	NOOP,
	randomId,
	showAlert,
} from '@/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileStatusEnum } from '../define-document-upload/define-document-upload.config';
import { LocalFile } from '../define-document-upload/define-document-upload.type';
import { getColumnsUpload } from './define-file-upload.config';
import { IProps } from './define-file-upload.type';

export const useLogic = (props: IProps) => {
	const { t } = useTranslation(['documentArea', 'message']);
	const { files, pageType, refTableId, onClose } = props;
	const [uploadFiles, setUploadFiles] = useState<LocalFile[]>([]);
	const [abortController, setAbortController] =
		useState<AbortController | null>(null);

	const uploadSingleFile = async (
		file: LocalFile,
		controller: AbortController
	) => {
		const fileId = file.id;

		setUploadFiles((prev) =>
			prev.map((f) =>
				f.id === fileId ? { ...f, status: FileStatusEnum.CONVERTING } : f
			)
		);

		try {
			const [convertedFile] = await convertAndCompressImages(
				[file.file as File],
				t,
				false
			);
			setUploadFiles((prev) =>
				prev.map((f) =>
					f.id === fileId
						? { ...f, progress: 0, status: FileStatusEnum.UPLOADING }
						: f
				)
			);
			const formData = new FormData();
			formData.append('refTableName', pageType);
			formData.append('refTableId', refTableId.toString());
			formData.append('reqUploadDocumentList[0].mutipartFile', convertedFile);
			formData.append('reqUploadDocumentList[0].fileType', FileTypeEnum.UPLOAD);
			formData.append(
				'reqUploadDocumentList[0].documentType',
				DocumentTypeEnum.C_0
			);
			await uploadDocument(
				formData,
				(progressEvent) => {
					if (progressEvent.total) {
						const percent = Math.round(
							(progressEvent.loaded * 100) / progressEvent.total
						);
						setUploadFiles((prev) =>
							prev.map((f) =>
								f.id === fileId
									? {
											...f,
											progress: Math.min(percent, 99),
										}
									: f
							)
						);
					}
				},
				controller.signal
			);

			setUploadFiles((prev) =>
				prev.map((f) =>
					f.id === fileId
						? { ...f, progress: 100, status: FileStatusEnum.DONE }
						: f
				)
			);
		} catch (error) {
			if (controller.signal.aborted) {
				console.warn('Upload cancelled');
			}
			setUploadFiles((prev) =>
				prev.map((f) =>
					f.id === fileId
						? { ...f, status: FileStatusEnum.ERROR, progress: 100 }
						: f
				)
			);
		}
	};

	// Upload sequentially each file
	const uploadSequentially = async (
		filesToUpload: LocalFile[],
		controller: AbortController
	) => {
		for (const file of filesToUpload) {
			if (controller.signal.aborted) break;
			await uploadSingleFile(file, controller);
		}
	};

	useEffect(() => {
		if (files.length > 0) {
			const convertFiles = async () => {
				const localFiles = files.map((file, index) => ({
					id: randomId() + index,
					file,
					progress: 0,
					status: FileStatusEnum.WAITING,
				})) as LocalFile[];
				setUploadFiles(localFiles);
				const controller = new AbortController();
				setAbortController(controller);

				// Start upload after setting file
				uploadSequentially(localFiles, controller);
			};
			convertFiles();
		}
	}, [files]);

	const uploadColumns = useMemo(() => getColumnsUpload(t), [t]);

	const handleCompleteUpload = () => {
		if (onClose) {
			onClose(); // close modal from parent
		}
	};

	const allCompleted = useMemo(() => {
		return (
			uploadFiles.length > 0 &&
			uploadFiles.every(
				(file) =>
					file.status === FileStatusEnum.DONE ||
					file.status === FileStatusEnum.ERROR
			)
		);
	}, [uploadFiles]);

	useEffect(() => {
		if (allCompleted) {
			const hasHeicHeif = files.some((file) => isHeicHeifFile(file.name));
			if (hasHeicHeif) {
				showAlert(t('validation:has-heic-heif-need-convert-format'));
			}
		}
	}, [allCompleted, t, files]);

	const handleAbortUpload = () => {
		if (abortController) {
			abortController.abort();
		}
	};

	const onCloseWithConfirm = () => {
		if (allCompleted) {
			onClose?.();
			return;
		}
		showAlert(
			t('documentArea:confirm-cancel-upload'),
			() => {
				// Check for HEIC/HEIF files in completed uploads before aborting
				const hasHeicHeif = uploadFiles
					.filter((file) => file.status === FileStatusEnum.DONE && file.file)
					.some((file) => isHeicHeifFile(file.file!.name));
				if (hasHeicHeif) {
					showAlert(t('validation:has-heic-heif-need-convert-format'));
				}
				handleAbortUpload();
				onClose?.();
			},
			NOOP
		);
	};

	return {
		t,
		uploadColumns,
		uploadFiles,
		handleCompleteUpload,
		allCompleted,
		onCloseWithConfirm,
	};
};
